import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrophyIcon, SpinnerGapIcon, FireIcon, ChartLineUpIcon, ShareNetworkIcon, CaretUpIcon } from "@phosphor-icons/react";
import { fetchTodaysRunner } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { QuickBuyLink } from "./TradeButton";
import { useWatchlist } from "../context/WatchlistContext";
import { useTrash, useCommunityTrash } from "../lib/useTrash";
import { TrashButton } from "./TrashButton";
import { StarIcon } from "@phosphor-icons/react";
import { TodaysRunnerShareCard } from "./TodaysRunnerShareCard";

const GOLD = "#F0C000";

const scoreColor = (s) => (s >= 70 ? GOLD : s >= 55 ? "#00E599" : s >= 40 ? "#22D3EE" : "#888");

const RunnerCard = ({ c, rank, onShare }) => {
  const { isWatched, toggle } = useWatchlist();
  const watched = isWatched(c.mint);
  const col = scoreColor(c.runnerScore);
  return (
    <div
      data-testid={`todays-runner-card-${c.mint}`}
      className="relative flex flex-col gap-2.5 border p-3.5 transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: rank === 1 ? `${GOLD}66` : "rgba(255,255,255,0.1)", background: rank === 1 ? `linear-gradient(135deg, ${GOLD}12, transparent 60%)` : "transparent" }}
    >
      <div className="flex items-center gap-2.5">
        {c.image ? (
          <img src={c.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
        ) : (
          <div className="w-9 h-9 flex items-center justify-center border border-white/10 shrink-0 text-muted-foreground"><TrophyIcon size={16} /></div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {rank === 1 && <span className="text-[9px] font-mono px-1 py-0.5 font-bold" style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD }}>TOP PICK</span>}
            <Link to={`/coin/${c.mint}`} className="font-heading font-bold truncate hover:text-neon transition-colors">${c.symbol || "token"}</Link>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground truncate">
            {fmtUsd(c.marketCap)} mc · {Math.round(c.ageHours)}h · conv {c.conviction}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-black text-2xl leading-none" style={{ color: col }}>{c.runnerScore}</div>
          <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">runner</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {c.themeMatch && (
          <span className="text-[9px] font-mono px-1.5 py-0.5" style={{ background: "#FF7A0022", border: "1px solid #FF7A0066", color: "#FF7A00" }}>
            🔥 "{c.themeMatch}" theme ×{c.themeCount}
          </span>
        )}
        {c.lpUnlocked && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-danger/20 border border-danger/60 text-danger font-bold">🔓 LP</span>}
        {c.lpLocked && <span className="text-[9px] font-mono px-1.5 py-0.5 font-bold" style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.6)", color: "#4ADE80" }}>🔒 LP</span>}
        {(c.reasons || []).slice(0, 2).filter((r) => !r.includes("theme")).map((r, i) => (
          <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 bg-white/5 border border-white/10 text-muted-foreground">{r}</span>
        ))}
      </div>

      <div className="flex items-center gap-1 pt-0.5">
        <QuickBuyLink coin={{ address: c.mint, url: c.url }} testid={`tr-buy-${c.mint}`} />
        <button onClick={() => toggle(c.mint)} title={watched ? "Remove from watchlist" : "Save to watchlist"} data-testid={`tr-watch-${c.mint}`} className="p-1.5 hover:bg-white/5 transition-colors">
          <StarIcon size={15} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
        </button>
        <button onClick={() => onShare(c)} title="Share this runner call" data-testid={`tr-share-${c.mint}`} className="p-1.5 text-muted-foreground hover:text-neon transition-colors">
          <ShareNetworkIcon size={15} weight="bold" />
        </button>
        <Link to={`/coin/${c.mint}`} title="Chart & score" data-testid={`tr-chart-${c.mint}`} className="p-1.5 text-muted-foreground hover:text-neon transition-colors">
          <ChartLineUpIcon size={15} weight="bold" />
        </Link>
        <TrashButton mint={c.mint} symbol={c.symbol} testid={`tr-trash-${c.mint}`} />
      </div>
    </div>
  );
};

export const TodaysRunner = () => {
  const [shareRunner, setShareRunner] = useState(null);
  const { isTrashed } = useTrash();
  const { isCommunityScam } = useCommunityTrash();
  const { data, isLoading } = useQuery({
    queryKey: ["todaysRunner"],
    queryFn: fetchTodaysRunner,
    refetchInterval: 90000,
    placeholderData: (p) => p,
  });
  const candidates = (data?.candidates || []).filter((c) => !isTrashed(c.mint) && !isCommunityScam(c.mint));
  const themeWatch = data?.themeWatch || data?.hotThemes || [];

  return (
    <div className="space-y-3 border-t border-white/10 pt-8" data-testid="todays-runner">
      <div className="flex items-center gap-2.5">
        <TrophyIcon size={24} weight="fill" style={{ color: GOLD }} />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter leading-none">
          COULD BE <span style={{ color: GOLD }}>TODAY'S RUNNER</span>
        </h2>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        The daily runner pattern: a <span style={{ color: GOLD }} className="font-semibold">fresh, organic survivor</span> with high
        conviction that fits the <span style={{ color: "#FF7A00" }} className="font-semibold">day's hot theme</span> — the cluster of
        similar names launching now (the ones that later spawn baby/spinoff copycats). Ranked by a composite Runner Score. Always DYOR.
      </p>

      {themeWatch.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="hot-themes">
          <span className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[#FF7A00]"><FireIcon size={13} weight="fill" /> Theme watch — heating fastest:</span>
          {themeWatch.map((t) => (
            <span key={t.theme} className="flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 border" style={{ background: "#FF7A0012", borderColor: "#FF7A0044", color: "#FF7A00" }}>
              "{t.theme}" ×{t.count}
              {t.change > 0 && <span className="flex items-center" style={{ color: "#4ADE80" }} title={`up ${t.change} since earlier today`}><CaretUpIcon size={10} weight="bold" />{t.change}</span>}
            </span>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-14"><SpinnerGapIcon size={28} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : candidates.length === 0 ? (
        <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6">
          No standout runner candidate right now — waiting for a fresh survivor that fits a hot theme.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {candidates.map((c, i) => <RunnerCard key={c.mint} c={c} rank={i + 1} onShare={setShareRunner} />)}
        </div>
      )}

      {shareRunner && <TodaysRunnerShareCard runner={shareRunner} open={!!shareRunner} onClose={() => setShareRunner(null)} />}
    </div>
  );
};
